
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, X } from 'lucide-react';

interface TeacherProfileImageUploadProps {
  formData?: any;
  setProfileImage?: (imageUrl: string) => void;
  handleImageUpload?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const TeacherProfileImageUpload = ({ 
  formData, 
  setProfileImage,
  handleImageUpload 
}: TeacherProfileImageUploadProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(formData?.profileImage || null);
  
  // Update preview when formData changes
  useEffect(() => {
    if (formData?.profileImage) {
      console.log('🖼️ TeacherProfileImageUpload: Setting preview URL:', formData.profileImage?.substring(0, 50) + '...');
      setPreviewUrl(formData.profileImage);
    }
  }, [formData?.profileImage]);
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('📤 TeacherProfileImageUpload: File change detected');
    
    if (handleImageUpload) {
      handleImageUpload(e);
    }
    
    const file = e.target.files?.[0];
    if (file) {
      console.log('📁 TeacherProfileImageUpload: Processing file:', file.name, 'Size:', file.size);
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        console.log('✅ TeacherProfileImageUpload: Image converted to base64, length:', result.length);
        setPreviewUrl(result);
        
        if (setProfileImage) {
          console.log('💾 TeacherProfileImageUpload: Calling setProfileImage with:', result.substring(0, 50) + '...');
          setProfileImage(result);
        }
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleRemoveImage = () => {
    console.log('🗑️ TeacherProfileImageUpload: Removing image');
    setPreviewUrl(null);
    if (setProfileImage) {
      setProfileImage('');
    }
  };

  // Get initials for fallback
  const getInitials = () => {
    const firstName = formData?.firstName || '';
    const lastName = formData?.lastName || '';
    return (firstName[0] || '') + (lastName[0] || '');
  };

  return (
    <div className="flex flex-col items-center">
      <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 mb-4 relative border-4 border-gray-200">
        {previewUrl ? (
          <>
            <img 
              src={previewUrl} 
              alt="Profile preview" 
              className="w-full h-full object-cover" 
              onLoad={() => console.log('🖼️ TeacherProfileImageUpload: Preview image loaded successfully')}
              onError={(e) => {
                console.log('❌ TeacherProfileImageUpload: Preview image failed to load, using fallback');
                setPreviewUrl(null);
              }}
            />
            <button
              type="button"
              onClick={handleRemoveImage}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-500 to-amber-300">
            {getInitials() ? (
              <span className="text-white text-2xl font-bold">
                {getInitials()}
              </span>
            ) : (
              <Upload className="w-8 h-8 text-white" />
            )}
          </div>
        )}
      </div>
      
      <input 
        type="file" 
        id="profileImage"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
      
      <Label htmlFor="profileImage">
        <Button type="button" variant="outline" size="sm" asChild>
          <span>
            <Upload className="h-4 w-4 mr-2" />
            {previewUrl ? 'Change Image' : 'Upload Image'}
          </span>
        </Button>
      </Label>
    </div>
  );
};

// Helper component for the label
const Label = ({ htmlFor, children }: { htmlFor: string, children: React.ReactNode }) => (
  <label htmlFor={htmlFor} className="cursor-pointer">
    {children}
  </label>
);
